/**
 * Source pipeline step that wraps literal LLM-provided rows into a typed
 * {@link Dataset}.
 *
 * Runs {@link inferColumnSchemas} over the full row set so downstream
 * operators (filter, derive, ...) see accurate column types without
 * additional boundary coercion. Mixed-type columns collapse to `'unknown'`
 * per {@link inferColumnSchemas} rules.
 *
 * Unlike operator steps this step does not consume an upstream Dataset —
 * `consumesInput` is `false` and the orchestrator passes `undefined` for
 * input.
 */
import z from 'zod';

import { inferColumnSchemas } from '#core/lib/transforms/column-schema.ts';

import { createDataset } from '../dataset.ts';
import type { PipelineStep } from '../step/types.ts';
import { InvalidSpecError } from '#plugin-chat/lib/tool/errors/index.ts';

const STEP_ID = 'registerLiteral';

const registerLiteralParamsSchema = z.object({
  rows: z
    .array(z.record(z.string(), z.unknown()))
    .describe('Literal rows to wrap in a Dataset. Must be non-empty; each row is a flat record.'),
  title: z.string().optional().describe('Optional human-readable title attached to the produced Dataset.meta.'),
});

type RegisterLiteralParams = z.infer<typeof registerLiteralParamsSchema>;

export const registerLiteralStep: PipelineStep<RegisterLiteralParams> = {
  id: STEP_ID,
  description:
    'Wrap LLM-provided literal rows into a Dataset with column schemas inferred from the row values (full-scan inference). Each row must contain only primitive values (string, number, boolean, null).',
  paramsSchema: registerLiteralParamsSchema,
  consumesInput: false,
  execute: async ({ rows, title }) => {
    if (!rows?.length) {
      throw new InvalidSpecError('registerLiteral requires at least one input data row.', {
        suggestions: ['Provide a non-empty input data rows array.'],
      });
    }
    rows.forEach((row, i) => {
      Object.entries(row).forEach(([key, value]) => {
        if (value !== null && typeof value === 'object') {
          throw new InvalidSpecError(
            `Row at index ${i} has a non-primitive value at key "${key}". Only primitive values (string, number, boolean, null) are allowed in literal rows.`,
            { suggestions: ['Flatten nested objects before calling registerLiteral.'] },
          );
        }
      });
    });
    return createDataset({
      rows,
      columns: inferColumnSchemas(rows),
    });
  },
};
